/*
 * Tykables — Quick View
 * -----------------------------------------------------------------------------
 * Lightweight modal loader. Intercepts clicks on [data-quick-view="{handle}"]
 * buttons in product cards, fetches /products/{handle}?view=quickview (rendered
 * by templates/product.quickview.liquid via layout:none), injects the returned
 * section markup into #tyk-qv-content, and re-parses <product-form> custom
 * elements so Dawn's AJAX add-to-cart + cart-drawer behavior takes over.
 */
(function () {
  'use strict';

  var MODAL_ID = 'tyk-qv-modal';
  var CONTENT_ID = 'tyk-qv-content';
  var OPEN_CLASS = 'is-open';
  var VISIBLE_CLASS = 'is-visible';
  var BODY_LOCK = 'tyk-qv-open';

  var modal = null;
  var content = null;
  var lastFocus = null;
  var currentHandle = null;
  var currentVariants = [];
  var currentVariant = null;
  var fetchAbort = null;

  function init() {
    modal = document.getElementById(MODAL_ID);
    content = document.getElementById(CONTENT_ID);
    if (!modal || !content) return;

    // Delegated trigger binding — one listener covers all card grids,
    // including grids inserted dynamically via filter AJAX refresh.
    document.addEventListener('click', onDocClick);

    // Close interactions.
    modal.addEventListener('click', function (e) {
      var t = e.target;
      if (!t) return;
      if (t.closest && t.closest('[data-tyk-qv-close]')) {
        e.preventDefault();
        close();
      }
    });

    // ESC to close.
    document.addEventListener('keydown', function (e) {
      if (!modal.classList.contains(VISIBLE_CLASS)) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      } else if (e.key === 'Tab') {
        trapFocus(e);
      }
    });

    // Close QV when an item is successfully added to cart. Dawn emits a
    // bubble on <product-form>; the AJAX response also flows through
    // pubsub.js. Listen generously so we don't miss an event variant.
    document.addEventListener('cart:added', close);
    document.addEventListener('on:cart-item:added', close);
    document.addEventListener('cart-notification:open', close);
    document.addEventListener('cart-drawer:open', close);
  }

  function onDocClick(e) {
    var btn = e.target && e.target.closest && e.target.closest('[data-quick-view]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var handle = btn.getAttribute('data-quick-view');
    if (!handle) return;
    open(handle, btn);
  }

  function open(handle, trigger) {
    if (!handle) return;

    lastFocus = trigger || document.activeElement;
    currentHandle = handle;

    // Show the shell immediately with skeleton.
    modal.hidden = false;
    modal.classList.add(OPEN_CLASS);
    modal.removeAttribute('inert');
    document.body.classList.add(BODY_LOCK);

    // Force reflow then add visible class so the transition plays.
    // eslint-disable-next-line no-unused-expressions
    modal.offsetHeight;
    modal.classList.add(VISIBLE_CLASS);

    focusFirstInteractive();
    loadContent(handle);
  }

  function close() {
    if (!modal.classList.contains(OPEN_CLASS)) return;

    if (fetchAbort) {
      try { fetchAbort.abort(); } catch (e) {}
      fetchAbort = null;
    }

    modal.classList.remove(VISIBLE_CLASS);
    modal.setAttribute('inert', '');
    document.body.classList.remove(BODY_LOCK);

    // Wait for transition, then hide + reset content.
    window.setTimeout(function () {
      if (!modal.classList.contains(VISIBLE_CLASS)) {
        modal.classList.remove(OPEN_CLASS);
        modal.hidden = true;
        resetContent();
      }
    }, 320);

    if (lastFocus && typeof lastFocus.focus === 'function') {
      try { lastFocus.focus(); } catch (e) {}
    }
    currentHandle = null;
    currentVariants = [];
    currentVariant = null;
  }

  function resetContent() {
    // Re-render the skeleton placeholder for the next open.
    content.innerHTML =
      '<div class="tyk-qv-modal__skeleton" aria-hidden="true">' +
      '  <div class="tyk-qv-modal__skel-img"></div>' +
      '  <div class="tyk-qv-modal__skel-lines">' +
      '    <div class="tyk-qv-modal__skel-line tyk-qv-modal__skel-line--sm"></div>' +
      '    <div class="tyk-qv-modal__skel-line tyk-qv-modal__skel-line--lg"></div>' +
      '    <div class="tyk-qv-modal__skel-line tyk-qv-modal__skel-line--md"></div>' +
      '    <div class="tyk-qv-modal__skel-line tyk-qv-modal__skel-line--md"></div>' +
      '    <div class="tyk-qv-modal__skel-line tyk-qv-modal__skel-line--btn"></div>' +
      '  </div>' +
      '</div>';
  }

  function loadContent(handle) {
    var url = '/products/' + encodeURIComponent(handle) + '?view=quickview';
    if (fetchAbort) {
      try { fetchAbort.abort(); } catch (e) {}
    }
    fetchAbort = new AbortController();

    fetch(url, {
      signal: fetchAbort.signal,
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Quick view fetch failed: ' + res.status);
        return res.text();
      })
      .then(function (html) {
        // Only inject if this is still the active handle.
        if (currentHandle !== handle) return;
        injectContent(html);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        // eslint-disable-next-line no-console
        console.warn('[tyk-qv] fetch failed:', err);
        content.innerHTML =
          '<div style="padding:24px;text-align:center;color:var(--tyk-text-soft,#5d79b4)">' +
          'Couldn\'t load this product. <a href="/products/' + handle + '" style="color:var(--tyk-red,#ef2b2d);">Open full page →</a></div>';
      });
  }

  function injectContent(html) {
    // Parse the returned HTML and extract the section wrapper. Shopify
    // wraps section output in <div id="shopify-section-...">; if that's
    // missing we just inject the whole response.
    var tmp = document.createElement('div');
    tmp.innerHTML = html;
    var section = tmp.querySelector('.shopify-section') || tmp;

    // Move children into our content node.
    content.innerHTML = '';
    var kids = Array.prototype.slice.call(section.childNodes);
    for (var i = 0; i < kids.length; i++) {
      content.appendChild(kids[i]);
    }

    // Execute any <script> tags (innerHTML doesn't run them).
    executeScripts(content);

    // Capture variant list for price/ATC updates.
    var variantsEl = content.querySelector('[data-tyk-qv-variants]');
    if (variantsEl) {
      try {
        currentVariants = JSON.parse(variantsEl.textContent.trim());
      } catch (e) {
        currentVariants = [];
      }
    }
    currentVariant = findSelectedVariant();

    wireVariantPicker();
    wireQuantity();
    wireGallery();

    // Focus the first actionable control inside the injected content.
    focusFirstInteractive();
  }

  function executeScripts(root) {
    var scripts = root.querySelectorAll('script');
    for (var i = 0; i < scripts.length; i++) {
      var old = scripts[i];
      // Skip JSON data blocks — just state, no execution.
      if ((old.type || '').toLowerCase() === 'application/json') continue;
      var s = document.createElement('script');
      for (var a = 0; a < old.attributes.length; a++) {
        var attr = old.attributes[a];
        s.setAttribute(attr.name, attr.value);
      }
      if (old.src) {
        s.src = old.src;
      } else {
        s.textContent = old.textContent;
      }
      old.parentNode.replaceChild(s, old);
    }
  }

  // ---------------------------------------------------------------------------
  // Variant picker wiring — updates selected variant id + price + button state.
  // ---------------------------------------------------------------------------
  function findSelectedVariant() {
    if (!currentVariants.length) return null;
    var selectedOptions = {};
    var inputs = content.querySelectorAll('[data-tyk-qv-option-input]:checked');
    for (var i = 0; i < inputs.length; i++) {
      var input = inputs[i];
      var name = input.name.match(/^options\[(.+)\]$/);
      if (name) selectedOptions[name[1]] = input.value;
    }
    for (var v = 0; v < currentVariants.length; v++) {
      var variant = currentVariants[v];
      var match = true;
      for (var o = 0; o < variant.options.length; o++) {
        var optName = null;
        // Shopify embeds options as option1/option2/option3 on the variant
        // but also exposes product.options_with_values via the form inputs.
        // Match by position: inputs are emitted in options_with_values order.
      }
      // Robust match: compare each variant.options array slot against the
      // input values keyed by their ORDER in the form.
      var orderedInputs = content.querySelectorAll('.tyk-qv__picker');
      if (orderedInputs.length === variant.options.length) {
        for (var j = 0; j < orderedInputs.length; j++) {
          var chk = orderedInputs[j].querySelector('[data-tyk-qv-option-input]:checked');
          if (!chk || chk.value !== variant.options[j]) {
            match = false;
            break;
          }
        }
      } else {
        match = false;
      }
      if (match) return variant;
    }
    return null;
  }

  function wireVariantPicker() {
    var inputs = content.querySelectorAll('[data-tyk-qv-option-input]');
    Array.prototype.forEach.call(inputs, function (input) {
      input.addEventListener('change', function () {
        // Toggle active class on the chip label
        var picker = input.closest('.tyk-qv__picker');
        if (picker) {
          Array.prototype.forEach.call(picker.querySelectorAll('.tyk-qv__chip'), function (c) {
            c.classList.remove('is-active');
          });
          var label = input.closest('.tyk-qv__chip');
          if (label) label.classList.add('is-active');
          var optLabel = picker.querySelector('[data-tyk-qv-option-label]');
          if (optLabel) optLabel.textContent = input.value;
        }
        currentVariant = findSelectedVariant();
        applyVariantToUI();
      });
    });
  }

  function applyVariantToUI() {
    var hidden = content.querySelector('[data-tyk-qv-variant-id]');
    var priceCurrent = content.querySelector('[data-tyk-qv-price-current]');
    var priceCompare = content.querySelector('[data-tyk-qv-price-compare]');
    var addBtn = content.querySelector('[data-tyk-qv-add-btn]');
    var addLabel = content.querySelector('[data-tyk-qv-add-label]');

    if (!currentVariant) {
      if (addBtn) addBtn.setAttribute('disabled', 'disabled');
      if (addLabel) addLabel.textContent = 'Unavailable';
      return;
    }
    if (hidden) hidden.value = currentVariant.id;
    if (priceCurrent) priceCurrent.textContent = formatMoney(currentVariant.price);
    if (priceCompare) {
      if (currentVariant.compare_at_price && currentVariant.compare_at_price > currentVariant.price) {
        priceCompare.textContent = formatMoney(currentVariant.compare_at_price);
        priceCompare.style.display = '';
      } else {
        priceCompare.style.display = 'none';
      }
    }
    if (addBtn) {
      if (currentVariant.available) {
        addBtn.removeAttribute('disabled');
        if (addLabel) addLabel.textContent = 'Add to cart';
      } else {
        addBtn.setAttribute('disabled', 'disabled');
        if (addLabel) addLabel.textContent = 'Sold out';
      }
    }
  }

  function formatMoney(cents) {
    // Prefer Shopify.formatMoney if present (cart-drawer loads it for us).
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        var fmt = (window.Shopify.money_format) || '${{amount}}';
        return window.Shopify.formatMoney(cents, fmt);
      } catch (e) {}
    }
    // Fallback — crude USD formatting. Swap in Shopify.formatMoney for i18n.
    var n = Number(cents) / 100;
    return '$' + n.toFixed(2);
  }

  // ---------------------------------------------------------------------------
  // Quantity steppers.
  // ---------------------------------------------------------------------------
  function wireQuantity() {
    var minus = content.querySelector('[data-tyk-qv-qty-minus]');
    var plus = content.querySelector('[data-tyk-qv-qty-plus]');
    var input = content.querySelector('[data-tyk-qv-qty-input]');
    if (!input) return;
    if (minus) minus.addEventListener('click', function () {
      var v = parseInt(input.value, 10) || 1;
      input.value = Math.max(1, v - 1);
    });
    if (plus) plus.addEventListener('click', function () {
      var v = parseInt(input.value, 10) || 1;
      input.value = v + 1;
    });
  }

  // ---------------------------------------------------------------------------
  // Simple gallery thumb → main slide switcher.
  // ---------------------------------------------------------------------------
  function wireGallery() {
    var thumbs = content.querySelectorAll('[data-tyk-qv-thumb]');
    if (!thumbs.length) return;
    Array.prototype.forEach.call(thumbs, function (thumb) {
      thumb.addEventListener('click', function () {
        var idx = thumb.getAttribute('data-tyk-qv-thumb');
        Array.prototype.forEach.call(thumbs, function (t) {
          var on = t.getAttribute('data-tyk-qv-thumb') === idx;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        var slides = content.querySelectorAll('[data-tyk-qv-slide]');
        Array.prototype.forEach.call(slides, function (s) {
          s.classList.toggle('is-active', s.getAttribute('data-tyk-qv-slide') === idx);
        });
      });
    });
  }

  // ---------------------------------------------------------------------------
  // Focus helpers.
  // ---------------------------------------------------------------------------
  function focusableNodes() {
    return modal.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
  }

  function focusFirstInteractive() {
    var nodes = focusableNodes();
    // Skip the backdrop (tabindex -1) and prefer the close button or first form control.
    var candidate = null;
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      if (n.classList && n.classList.contains('tyk-qv-modal__backdrop')) continue;
      candidate = n;
      break;
    }
    if (candidate) {
      try { candidate.focus({ preventScroll: true }); } catch (e) { candidate.focus(); }
    }
  }

  function trapFocus(e) {
    var nodes = focusableNodes();
    if (!nodes.length) return;
    var first = nodes[0];
    var last = nodes[nodes.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
